"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { EcommerceTermsProvider, useEcommerceTerms } from "@/integrations/wordpress/WordPressEcommerceTermsProvider";
import { WP_Term_Query } from "@/integrations/wordpress/wp_term_query";
const fallbackCategories = [{
  numeral: "I",
  name: "Comedy Specials",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718611/img/comedian_character.png",
  alt: "Comedian performing on stage"
}, {
  numeral: "II",
  name: "Live Shows",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718611/img/live_crowd.webp",
  alt: "Crowd at a live comedy event"
}, {
  numeral: "III",
  name: "Odd Merchandise",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718611/img/merch_preview.png",
  alt: "Oddhatter branded merchandise"
}, {
  numeral: "IV",
  name: "Blog Archives",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718611/img/blog_hero.png",
  alt: "Vintage microphone and notebook"
}];
const romanNumerals = ["I", "II", "III", "IV"];
const layoutConfig = [{
  colSpan: "lg:col-span-5",
  aspect: "aspect-[4/5]",
  offset: "",
  placement: "lg:col-start-1 lg:row-start-1"
}, {
  colSpan: "lg:col-span-4",
  aspect: "aspect-square",
  offset: "lg:mt-24",
  placement: "lg:col-start-6 lg:row-start-1"
}, {
  colSpan: "lg:col-span-3",
  aspect: "aspect-[3/5]",
  offset: "lg:-mt-8",
  placement: "lg:col-start-10 lg:row-start-1"
}, {
  colSpan: "lg:col-span-6",
  aspect: "aspect-[5/3]",
  offset: "lg:mt-8",
  placement: "lg:col-start-2 lg:row-start-2"
}];
function CategoryTile({
  numeral,
  name,
  image,
  alt,
  link,
  srcset,
  sizes,
  colSpan,
  aspect,
  offset,
  placement,
  index
}: {
  numeral: string;
  name: string;
  image: string;
  alt: string;
  link: string;
  srcset?: string;
  sizes?: string;
  colSpan: string;
  aspect: string;
  offset: string;
  placement: string;
  index: number;
}) {
  return <Link data-index={index} to={link} className={`group relative block col-span-12 ${colSpan} ${placement} ${offset} overflow-hidden rounded-md transition-all duration-500`}>
      <div className={`relative w-full ${aspect} overflow-hidden bg-slate-800`}>
        {srcset && sizes ? <img src={image} data-wvc-srcset={srcset} data-wvc-sizes={sizes} alt={alt} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105" /> : <img src={image} data-wvc-srcset={`${image}-wvc-srcset`} data-wvc-sizes={`${image}-wvc-sizes`} alt={alt} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105" />}

        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" aria-hidden="true" />

        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-0 bg-yellow-700/90 transition-all duration-700 ease-out group-hover:h-full" aria-hidden="true" />

        <span className="absolute top-6 left-7 font-default text-2xl font-bold tracking-widest text-foreground transition-colors duration-500 md:text-3xl">
          {numeral}
        </span>

        <div className="absolute bottom-6 left-7 right-7 transition-colors duration-500 group-hover:text-white">
          <h3 className="font-default text-2xl font-bold text-foreground md:text-[28px] md:leading-tight">
            {name}
          </h3>
          <span className="mt-2 inline-flex items-center gap-2 text-sm text-muted-foreground transition-all duration-500">
            <span className="relative">
              Explore
              <span className="absolute -bottom-0.5 left-0 h-px w-0 bg-purple-200 transition-all duration-500 group-hover:w-full" aria-hidden="true" />
            </span>
            <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1" />
          </span>
        </div>
      </div>
    </Link>;
}
function RitualCategoriesGrid() {
  const {
    terms,
    loading,
    error
  } = useEcommerceTerms();
  if (loading) {
    return <div className="grid grid-cols-12 gap-6 md:gap-8">
        {layoutConfig.map((cfg, i) => <div key={i} data-index={i} className={`col-span-12 ${cfg.colSpan} ${cfg.placement} ${cfg.offset}`}>
            <div className={`w-full ${cfg.aspect} animate-pulse rounded-md bg-slate-800`} />
          </div>)}
      </div>;
  }
  if (error || terms.length === 0) {
    return <div className="grid grid-cols-12 gap-6 md:gap-8">
        {fallbackCategories.map((cat, i) => {
        const cfg = layoutConfig[i % layoutConfig.length];
        return <CategoryTile key={cat.name} index={i} numeral={cat.numeral} name={cat.name} image={cat.image} alt={cat.alt} link="#" colSpan={cfg.colSpan} aspect={cfg.aspect} offset={cfg.offset} placement={cfg.placement} />;
      })}
      </div>;
  }
  const displayTerms = terms.slice(0, 4);
  return <div className="grid grid-cols-12 gap-6 md:gap-8">
      {displayTerms.map((term, i) => {
      const cfg = layoutConfig[i % layoutConfig.length];
      const fallback = fallbackCategories[i % fallbackCategories.length];
      const image = term.image_url || fallback.image;
      return <CategoryTile key={term.id} index={i} numeral={romanNumerals[i] || fallback.numeral} name={term.name} image={image} alt={term.name} link={term.link || "#"} colSpan={cfg.colSpan} aspect={cfg.aspect} offset={cfg.offset} placement={cfg.placement} />;
    })}
    </div>;
}
const wp_term_query = new WP_Term_Query({
  taxonomy: "product_cat",
  per_page: 4,
  orderby: "name",
  order: "asc"
});
export default function Categories() {
  return <section data-section-id="3873" id="odd-categories" className="relative w-full bg-neutral-900 py-20 md:py-28 lg:py-36">
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="mx-auto max-w-2xl text-center">
          <p className="font-default text-xs font-medium uppercase tracking-[0.3em] text-slate-400">
            The Playground
          </p>
          <h2 className="mt-5 font-default text-4xl font-bold leading-[1.05] tracking-tight text-purple-200 md:text-5xl lg:text-6xl">
            Oddhatter, In Four{" "}
            <em className="font-default italic font-bold text-yellow-700">Acts</em>
          </h2>
          <p className="mt-5 font-default text-base leading-relaxed text-slate-400 md:text-lg">
            A curated collection of chaos and comedy — designed for the bold.
          </p>
        </div>

        <div className="mt-16 md:mt-20 lg:mt-24">
          <EcommerceTermsProvider className="the-ritual-categories-wrapper" wp_term_query={wp_term_query}>
            <RitualCategoriesGrid />
          </EcommerceTermsProvider>
        </div>
      </div>
    </section>;
}